import { DeviceRemovedPromptData } from '../services/useTriggerPromptsOnMediaSourcesUpdate'
import { Button } from '@/UIComponents2/Button'
import { AccentBadge } from '@/components/AccentBadge'
import SpecialSpeakerIcon from '@/CustomIcons/SpecialSpeakerIcon'
import SpecialMicrophoneIcon from '@/CustomIcons/SpecialMicrophoneIcon'
import CameraIcon from '@/CustomIcons/CameraIcon'
import { DialogBody, DialogSubtitle, DialogTitle } from '@/components/Dialog'
import DialogActionButtonsSection from './components/DialogActionButtonsSection'
import { Trans, useTranslation } from 'react-i18next'
import { SettingsTab } from '@/components/SessionSettings/SessionSettings'
import { sessionSettingsTrigger } from '@/pages/SessionPage/SessionPage'

export default function MediaDeviceRemovedPrompt({
    deviceRemovedPromptData,
    onDefaultClose,
}: {
    deviceRemovedPromptData: DeviceRemovedPromptData
    onDefaultClose: () => void
}) {
    const { t } = useTranslation()

    const handleOpenSessionSettings = (settingsTab: SettingsTab) => {
        onDefaultClose()
        sessionSettingsTrigger.trigger({ tab: settingsTab })
    }

    const generateDialogTitle = (deviceRemovedPromptData: DeviceRemovedPromptData) => {
        const { deviceType } = deviceRemovedPromptData
        if (deviceType === 'audioInput') {
            return t('Input device was removed')
        } else if (deviceType === 'audioOutput') {
            return t('Output device was removed')
        } else if (deviceType === 'video') {
            return t('Camera was removed')
        } else {
            return t('Device was removed')
        }
    }

    const generateDialogParagraph = (deviceRemovedPromptData: DeviceRemovedPromptData) => {
        const { deviceType } = deviceRemovedPromptData
        if (deviceType === 'audioInput') {
            return (
                <Trans
                    i18nKey="You can manage audio inputs in <0>Audio Settings</0>"
                    t={t}
                    components={[<Button variant="link" onClick={() => handleOpenSessionSettings('audio')} />]}
                />
            )
        }
        if (deviceType === 'audioOutput') {
            return (
                <Trans
                    i18nKey="You can change the audio device later in <0>Audio Settings</0>"
                    t={t}
                    components={[<Button variant="link" onClick={() => handleOpenSessionSettings('audio')} />]}
                />
            )
        }
        if (deviceType === 'video') {
            return (
                <Trans
                    i18nKey="You can change the video device later in <0>Video Settings</0>"
                    t={t}
                    components={[<Button variant="link" onClick={() => handleOpenSessionSettings('video')} />]}
                />
            )
        }

        return null
    }

    const icon = (deviceType: 'audioInput' | 'audioOutput' | 'video') => {
        if (deviceType === 'audioInput') {
            return <SpecialMicrophoneIcon gray />
        } else if (deviceType === 'audioOutput') {
            return <SpecialSpeakerIcon gray />
        } else if (deviceType === 'video') {
            return <CameraIcon style={{ fontSize: '30px' }} />
        }
    }

    return (
        <div className="flex flex-col w-full">
            <DialogBody className="flex flex-col gap-6">
                <AccentBadge variant="gray">{icon(deviceRemovedPromptData.deviceType)}</AccentBadge>
                <div>
                    <DialogTitle className="pb-1">{generateDialogTitle(deviceRemovedPromptData)}</DialogTitle>
                    <DialogSubtitle>{deviceRemovedPromptData.deviceLabel}</DialogSubtitle>
                </div>

                <div className="t-base-light">{generateDialogParagraph(deviceRemovedPromptData)}</div>
            </DialogBody>

            <DialogActionButtonsSection rightButtonText={t('OK')} onRightButtonClicked={onDefaultClose} />
        </div>
    )
}
